import * as React from "react";
import { Form, Option, Checkbox, useFormApi, useFormState } from "informed";
import { v4 } from "uuid";
import Modal, { ModalSize } from "../../misc/components/modal";
import { Dropdown } from "../../forms/informedv2/Dropdown";
import { TextBox } from "../../forms/informedv2/TextBox";
import { EDiarySessionConfiguration } from "./EDiarySessionConfiguration";
import { Required } from "../../forms/validation/Required";
import { ScheduleSession, } from "./VisitSchedule";
import { DeviceTypes } from "../../misc/api/apiTypes";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { SessionScheduleActionContextProvider, useSessionScheduleActionContext } from "./SessionScheduleActionContext";
import { ActionType } from "./DiarySessionFlow/DiarySessionFlow";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { MultiSelect } from "../../forms/informedv2/MultiSelect";
import { Study } from "webportalclient";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";

export interface SessionConfigurationProps {
    study: Study;
    possibleRequiredSessions: any[];
    possiblePreSessions: any[];
    sessionItem: ScheduleSession;
    onComplete: (session: ScheduleSession) => void;
    onClose: () => void;
    onClone: () => void;
}

interface SessionConfigurationForm {
    Label: string;
    Rules: string;
    Description: string;
    Rolling: boolean;
    Mandatory: boolean;
    Disabled: boolean;
    Group: string;
    SessionType: SessionTypes;
    DeviceType: DeviceTypes;
    RequiredSessions: any[];
    PreSessionItemId: string;
    Cohort: string;
    AfterRandomisation: boolean;
}

interface EDiarySessionConfigurationForm extends SessionConfigurationForm {
    StartWindow: string;
    EndWindow: string;
    ResetDiaryOnUpload: boolean;
}

export const SessionConfiguration = ({ study, possibleRequiredSessions, sessionItem, possiblePreSessions, onComplete, onClose, onClone }: SessionConfigurationProps) => {
    const initialFormValues: EDiarySessionConfigurationForm = {
        Label: sessionItem?.label,
        Description: sessionItem?.description,
        Rules: sessionItem?.rules,
        Rolling: sessionItem?.rolling ?? false,
        Mandatory: sessionItem?.isMandatory ?? false,
        Disabled: sessionItem?.disabled ?? false,
        SessionType: sessionItem?.sessionType,
        DeviceType: sessionItem?.deviceType,
        RequiredSessions: sessionItem?.requiredSessions,
        StartWindow: sessionItem?.eDiary?.startWindow,
        EndWindow: sessionItem?.eDiary?.endWindow,
        ResetDiaryOnUpload: sessionItem?.eDiary?.resetDiaryOnUpload,
        PreSessionItemId: sessionItem?.preSessionItemId,
        Group:  sessionItem?.group,
        Cohort: sessionItem?.cohort,
        AfterRandomisation: sessionItem?.afterRandomisation
    }

    return (
        <Modal show={true} header="Session Configuration" size={ModalSize.Large} closeModalHandler={onClose} automationId="SessionConfigurationModal">
            <ErrorBoundary>
                <SessionScheduleActionContextProvider sessionItem={sessionItem}>
                    <Form<EDiarySessionConfigurationForm> noValidate initialValues={initialFormValues}>
                        <SessionConfigurationCore
                            study={study}
                            possibleRequiredSessions={possibleRequiredSessions}
                            possiblePreSessions={possiblePreSessions}
                            id={sessionItem?.id ?? v4()}
                            isCreate={!sessionItem}
                            onComplete={onComplete}
                            onClone={onClone}
                        />
                    </Form>
                </SessionScheduleActionContextProvider>
            </ErrorBoundary>
        </Modal>
    );
}

export interface SessionConfigurationCoreProps {
    id: string;
    study: Study;
    possibleRequiredSessions: any[];
    possiblePreSessions: any[];
    isCreate: boolean;
    onComplete: (session: ScheduleSession) => void;
    onClone: () => void;
}

export const SessionConfigurationCore = ({ id, study, possibleRequiredSessions, possiblePreSessions, isCreate, onComplete, onClone }: SessionConfigurationCoreProps) => {
    const [sessionTypes, setSessionTypes] = React.useState([]);
    const [deviceTypes, setDeviceTypes] = React.useState([]);

    const { actions } = useSessionScheduleActionContext();
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const init = async () => {
            const sessionTypeResult = await legacyClient.getStudySessionTypes(study.id);
            setSessionTypes(sessionTypeResult);
            const deviceTypeResult = await legacyClient.getStudyDeviceTypes(study.id);
            setDeviceTypes(deviceTypeResult);
        }

        init();
    }, [study.id]);

    const formApi = useFormApi<EDiarySessionConfigurationForm>();
    const state = useFormState();

    const onSubmit = async () => {
        formApi.setFormError(undefined);
        for (const action of actions) {
            if (!action.ActionValue && (action.ActionType === ActionType.questionnaire || action.ActionType === ActionType.compliance || action.ActionType === ActionType.alert || action.ActionType === ActionType.transmission)) {
                formApi.setFormError("Questionnaire, Compliance, Alert and Transmission session actions require a value");
            }
            const transmissionSessionActions = actions.filter(item => item.ActionType === ActionType.transmission),
                transmissionCount = transmissionSessionActions.length;
            if (transmissionCount > 1) {
                formApi.setFormError("Only one Transmission Session Action Item can be added per session. Please remove any additional Transmission Session Action Items.");
            }
        }

        formApi.validate();
        let state = formApi.getState();

        if (state.values.DeviceType === DeviceTypes.In2itiveP230EDiary && actions.length === 0) {
            formApi.setFormError("e-Diary configuration cannot be loaded for the study. The session cannot be saved.");
        }

        formApi.validate();
        state = formApi.getState();

        if (state.invalid)
            return;

        const session: ScheduleSession = {
            id: id,
            label: state.values.Label,
            isMandatory: state.values.Mandatory,
            rules: state.values.Rules,
            description: state.values.Description,
            disabled: state.values.Disabled,
            rolling: state.values.Rolling,
            deviceType: state.values.DeviceType,
            sessionType: state.values.SessionType,
            requiredSessions: state.values.RequiredSessions,
            preSessionItemId: state.values.PreSessionItemId,
            group: state.values.Group,
            cohort: state.values.Cohort,
            afterRandomisation: state.values.AfterRandomisation,
            eDiary: {
                startWindow: state.values.StartWindow,
                endWindow: state.values.EndWindow,
                resetDiaryOnUpload: state.values.ResetDiaryOnUpload,
                actions: actions.map(a => ({
                    id: a.Id,
                    actionType: a.ActionType,
                    actionValue: a.ActionValue
                })),
            }
        }

        onComplete(session);
    }

    return (
        <>
            <div className="modal-body">
                <Alert show={state.error} category={AlertCategoryEnum.Danger}>
                    {state.error}
                </Alert>
                <div className="row">
                    <div className="form-group col-md-6 col-xs-6">
                        <label htmlFor="Label">Label</label>
                        <TextBox id="Label" field="Label" validate={v => Required(v)} />
                    </div>
                    <div className="form-group col-md-6 col-xs-6">
                        <label htmlFor="Group">Group</label>
                        <TextBox id="Group" field="Group"  />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-6 col-sm-6 col-xs-6">
                        <label htmlFor="Rolling">Rolling</label>
                        <p className="help-block">Can be continuously re-configured and performed even if it has already been used.</p>
                        <Checkbox id="Rolling" field="Rolling" />&nbsp;
                    </div>
                    <div className="form-group col-md-3 col-sm-3 col-xs-6">
                        <label htmlFor="Mandatory">Mandatory</label>
                        <p className="help-block">Required to be performed in schedule</p>
                        <Checkbox id="Mandatory" field="Mandatory" />&nbsp;
                    </div>
                    <div className="form-group col-md-3 col-sm-3 col-xs-6">
                        <label htmlFor="Disabled">Disabled</label>
                        <p className="help-block">Disabled from use in the schedule</p>
                        <Checkbox id="Disabled" field="Disabled" />&nbsp;
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="Description">Description</label>
                        <TextBox id="Description" field="Description" />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="Rules">Rules</label>
                        <TextBox id="Rules" field="Rules" />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-6 col-xs-12">
                        <label htmlFor="SessionType">Session Type</label>
                        <Dropdown
                            id="SessionType"
                            field="SessionType"
                            validate={Required}
                        >
                            <Option
                                key={"OptionBlank"}
                                value={[]}
                            ></Option>
                            {sessionTypes.map(st => (
                                <Option
                                    key={`Option${st.Id}`}
                                    value={st.Id}
                                >
                                    {st.Name}
                                </Option>
                            ))}
                        </Dropdown>
                    </div>
                    <div className="form-group col-md-6 col-xs-12">
                        <label htmlFor="DeviceType">Device Type</label>
                        <Dropdown
                            id="DeviceType"
                            field="DeviceType"
                            validate={Required}
                        >
                            <Option
                                key={"OptionBlank"}
                                value={[]}
                            ></Option>
                            {deviceTypes.filter(dt => dt.RelatedSessionTypes.includes(state.values.SessionType)).map(dt => (
                                <Option
                                    key={`Option${dt.Id}`}
                                    value={dt.Id}
                                >
                                    {dt.Name}
                                </Option>
                            ))}
                        </Dropdown>
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="RequiredSessions">Required Sessions</label>
                        <MultiSelect
                            id="RequiredSessions"
                            field="RequiredSessions"
                            options={possibleRequiredSessions}
                            labelKey="Label"
                            valueKey="Id"
                            multi={true}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="preSessionItemId">Pre Session</label>
                        <Dropdown
                            id="preSessionItemId"
                            field="PreSessionItemId"
                        >
                            <Option
                                key={"OptionBlank"}
                                value={[]}
                            ></Option>
                            {possiblePreSessions.map(s => (
                                <Option
                                    key={`Option${s.Id}`}
                                    value={s.Id}
                                >
                                    {s.Label}
                                </Option>
                            ))}
                        </Dropdown>
                    </div>
                </div>
                {state.values.DeviceType === DeviceTypes.In2itiveP230EDiary ? <EDiarySessionConfiguration study={study} /> : null}
                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="Cohort">Cohort</label>
                        <TextBox id="Cohort" field="Cohort" />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-6 col-sm-6 col-xs-6">
                        <label htmlFor="AfterRandomisation">Available only after randomisation</label>
                        <p className="help-block">The session will only be available for selection after randomisation of the subject</p>
                        <Checkbox id="AfterRandomisation" field="AfterRandomisation" />&nbsp;
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                {!isCreate ? <button className="btn btn-primary btn-outline" onClick={onClone} data-automationid="SessionConfiguration_Clone">Clone</button> : null}
                <button className="btn btn-primary" onClick={onSubmit} data-automationid="SessionConfiguration_Save">Save</button>
            </div>
        </>
    );
}
